import { computed, ref } from 'vue'

import notificationController from '../controllers/notification.controller.js'

import { NOTIFICATION_BADGE_MAX, NOTIFICATION_PAGE_SIZE } from '../config/notification.config.js'

// Module-scoped singletons: the header bell and the notification center read
// the same reactive state, so a store is unnecessary (architecture decision —
// light, frequently-updated data shared across views).
const items = ref([])
const total = ref(0)
const unreadCount = ref(0)
const loading = ref(false)

function nowIso() {
  return new Date().toISOString()
}

export function useNotifications() {
  const hasUnread = computed(() => unreadCount.value > 0)

  /** Capped badge label — `''` when nothing is unread, `99+` past the cap. */
  const badge = computed(() => {
    if (unreadCount.value <= 0) return ''
    return unreadCount.value > NOTIFICATION_BADGE_MAX ? `${NOTIFICATION_BADGE_MAX}+` : String(unreadCount.value)
  })

  /**
   * Load a page of notifications and refresh `total` + `unreadCount` from the
   * same response. Replaces the list by default; pass `append: true` for
   * load-more / infinite scroll. Leaves state untouched when the load fails
   * (controller toasts and returns `null`).
   *
   * @param {{ unread?: boolean, page?: number, pageSize?: number, append?: boolean }} [options]
   * @returns {Promise<boolean>} `true` when a page loaded, `false` on failure.
   */
  async function load({ unread = false, page = 1, pageSize = NOTIFICATION_PAGE_SIZE, append = false } = {}) {
    loading.value = true
    const res = await notificationController.get.notifications({ unread, page, pageSize })
    if (res) {
      items.value = append ? [...items.value, ...res.notifications] : res.notifications
      total.value = res.total
      unreadCount.value = res.unreadCount
    }
    loading.value = false
    return Boolean(res)
  }

  /**
   * Refresh only the unread badge count — the cheap poll for the bell. Leaves
   * the count untouched on failure (silent background call).
   *
   * @returns {Promise<number>} The current unread count.
   */
  async function refreshUnreadCount() {
    const count = await notificationController.get.unreadCount()
    if (count != null) unreadCount.value = count
    return unreadCount.value
  }

  /**
   * Mark one notification read, updating its row and decrementing the badge.
   * No-op when the row is already read.
   *
   * @param {string} id
   * @returns {Promise<boolean>}
   */
  async function markRead(id) {
    const row = items.value.find((notification) => notification._id === id)
    if (row?.read) return true

    const updated = await notificationController.update.read(id)
    if (!updated) return false

    if (row) {
      row.read = true
      row.readAt = updated.readAt ?? nowIso()
    }
    if (unreadCount.value > 0) unreadCount.value -= 1
    return true
  }

  /**
   * Mark every notification read and clear the badge.
   *
   * @returns {Promise<boolean>}
   */
  async function markAllRead() {
    const ok = await notificationController.update.readAll()
    if (!ok) return false

    const readAt = nowIso()
    for (const row of items.value) {
      if (!row.read) {
        row.read = true
        row.readAt = readAt
      }
    }
    unreadCount.value = 0
    return true
  }

  /**
   * Mark many notifications read. Skips rows that are already read.
   *
   * @param {string[]} ids
   * @returns {Promise<boolean>}
   */
  async function markReadMany(ids) {
    let ok = true
    for (const id of ids) {
      const result = await markRead(id)
      if (!result) ok = false
    }
    return ok
  }

  /**
   * Delete many notifications, dropping rows and adjusting counts.
   *
   * @param {string[]} ids
   * @returns {Promise<boolean>}
   */
  async function removeMany(ids) {
    let ok = true
    for (const id of ids) {
      const result = await remove(id)
      if (!result) ok = false
    }
    return ok
  }

  /**
   * Delete one notification, dropping the row and adjusting both counts.
   *
   * @param {string} id
   * @returns {Promise<boolean>}
   */
  async function remove(id) {
    const row = items.value.find((notification) => notification._id === id)

    const ok = await notificationController.delete.notification(id)
    if (!ok) return false

    items.value = items.value.filter((notification) => notification._id !== id)
    if (total.value > 0) total.value -= 1
    if (row && !row.read && unreadCount.value > 0) unreadCount.value -= 1
    return true
  }

  /** Clear all notification state — call on logout. */
  function reset() {
    items.value = []
    total.value = 0
    unreadCount.value = 0
    loading.value = false
  }

  return {
    items,
    total,
    unreadCount,
    loading,
    hasUnread,
    badge,
    load,
    refreshUnreadCount,
    markRead,
    markReadMany,
    markAllRead,
    remove,
    removeMany,
    reset,
  }
}
